import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import toast from 'react-hot-toast';
import BackBtn from '../components/BackBtn';

const API = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API || '';

export default function ScheduledTasks() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ task_id: '', command: '', run_at: '', repeat_seconds: '' });
  const [loading, setLoading] = useState(false);

  useLiveEvents((event) => {
    if (event.event === 'scheduled_tasks' && event.device_id === deviceId) {
      setTasks(event.tasks || []);
      setLoading(false);
    }
  });

  const fetchTasks = async () => {
    setLoading(true);
    const res = await fetch(`${API}/cmd/${deviceId}/list_scheduled`, {
      method: 'POST', headers: authHeaders(),
    });
    if (!res.ok) { toast.error('Device offline'); setLoading(false); }
  };

  const scheduleTask = async (e) => {
    e.preventDefault();
    if (!form.task_id || !form.command || !form.run_at) {
      return toast.error('Fill in Task ID, Command, and Run At');
    }
    const body = {
      task_id: form.task_id,
      command: form.command,
      run_at: new Date(form.run_at).toISOString(),
      repeat_seconds: form.repeat_seconds ? parseInt(form.repeat_seconds) : null,
    };
    const res = await fetch(`${API}/cmd/${deviceId}/schedule_task`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      toast.success('Task scheduled');
      setForm({ task_id: '', command: '', run_at: '', repeat_seconds: '' });
      fetchTasks();
    } else {
      toast.error((await res.json()).detail || 'Failed');
    }
  };

  const cancelTask = async (task_id) => {
    if (!window.confirm(`Cancel task "${task_id}"?`)) return;
    const res = await fetch(`${API}/cmd/${deviceId}/cancel_task`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id }),
    });
    if (res.ok) { toast.success('Task cancelled'); fetchTasks(); }
    else toast.error('Failed');
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
        {label}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <BackBtn onClick={() => navigate(`/device/${deviceId}`)} />
        <h2 style={{ margin: 0 }}>Scheduled Tasks</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Create form */}
        <div style={card}>
          <h3 style={{ marginBottom: 14 }}>Schedule New Task</h3>
          <form onSubmit={scheduleTask}>
            {field('task_id', 'Task ID', 'text', 'e.g. daily-report')}
            {field('command', 'Command', 'text', 'e.g. screenshot_now')}
            {field('run_at', 'Run At', 'datetime-local')}
            {field('repeat_seconds', 'Repeat Every (seconds, optional)', 'number', 'Leave blank for one-shot')}
            <button type="submit"
              style={{ width: '100%', padding: '9px 0', background: 'var(--accent)',
                       border: 'none', borderRadius: 6, color: '#fff',
                       cursor: 'pointer', fontWeight: 600, marginTop: 4 }}>
              Schedule Task
            </button>
          </form>
        </div>

        {/* Task list */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 14 }}>
            <h3>Active Tasks</h3>
            <button onClick={fetchTasks} disabled={loading}
              style={{ padding: '5px 12px', background: 'var(--border)', border: 'none',
                       borderRadius: 5, color: 'var(--text)', cursor: 'pointer', fontSize: 12 }}>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {tasks.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
              No tasks. Click Refresh to load.
            </div>
          ) : (
            tasks.map(t => (
              <div key={t.task_id}
                style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>{t.task_id}</span>
                  <button onClick={() => cancelTask(t.task_id)}
                    style={{ padding: '2px 10px', background: 'var(--red)', border: 'none',
                             borderRadius: 4, color: '#fff', cursor: 'pointer', fontSize: 11 }}>
                    Cancel
                  </button>
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 3 }}>
                  cmd: {t.command} | run_at: {t.run_at}
                  {t.repeat_seconds && ` | repeats every ${t.repeat_seconds}s`}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const card = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 10, padding: 20,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', background: 'var(--bg)',
  border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--text)', fontSize: 13,
};
